'use client';

import { RoundRecord } from '@/types/game';
import { getTier, TIER_BG } from '@/lib/game-logic';
import { useTranslation } from '@/lib/i18n/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Logo } from '@/components/Logo';

interface GameHeaderProps {
  round: number;
  totalRounds: number;
  score: number;
  history: RoundRecord[];
}

export function GameHeader({ round, totalRounds, score, history }: GameHeaderProps) {
  const { t } = useTranslation();

  return (
    <header className="shrink-0 border-b border-line bg-surface">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2.5">
        <Logo className="text-lg" />
        <div className="flex items-center gap-4">
          <LanguageSelector />
          <div className="text-right leading-none">
            <p className="mb-1 text-[10px] uppercase tracking-widest text-muted">{t('score')}</p>
            <p className="font-mono text-lg font-bold tabular-nums text-accent">{score}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 pb-2.5">
        <div
          className="flex gap-1.5"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={totalRounds}
          aria-valuenow={round}
          aria-label={`${t('round')} ${round}/${totalRounds}`}
        >
          {Array.from({ length: totalRounds }, (_, i) => {
            const done = history[i];
            const color = done
              ? TIER_BG[getTier(done.result.percentageError)]
              : i === round - 1
                ? 'bg-fg'
                : 'bg-line';
            return <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${color}`} />;
          })}
        </div>
        <p className="mt-1.5 text-[11px] uppercase tracking-widest text-muted">
          {t('round')} <span className="font-mono text-fg">{round}</span>/{totalRounds}
        </p>
      </div>
    </header>
  );
}
